import { useContext, useEffect, useState } from "react";
import { LiaMoneyBillWaveSolid } from "react-icons/lia";
import { getData } from "src/actions/getData";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import Hintbox from "src/components/Hintbox";
import userContext from "src/context/userContext";

// ثبت کامپوننت‌های مورد نیاز Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

export default function SellerDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartData, setChartData] = useState({ sales: null, count: null });
    const { user } = useContext(userContext)

    const numberWithCommas = (x) => {
        if (!x && x !== 0) return "-";
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // تابع برای محاسبه آمار از روی دیتای خام
    const calculateStats = (data) => {
        if (!data || data.length === 0) return null;

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        // فیلتر کردن داده‌ها بر اساس تاریخ
        const salesLast7Days = data.filter(item => {
            const itemDate = new Date(item.order_date);
            return itemDate >= sevenDaysAgo;
        });

        const salesLast30Days = data.filter(item => {
            const itemDate = new Date(item.order_date);
            return itemDate >= thirtyDaysAgo;
        });

        // محاسبه مجموع فروش
        const totalSales7d = salesLast7Days.reduce((sum, item) => sum + parseInt(item.item_total || 0), 0);
        const totalSales30d = salesLast30Days.reduce((sum, item) => sum + parseInt(item.item_total || 0), 0);

        // محاسبه تعداد فروش
        const count7d = salesLast7Days.length;
        const count30d = salesLast30Days.length;

        // پیدا کردن محصولات پرفروش
        const getTopProducts = (salesData) => {
            const productSales = {};

            salesData.forEach(item => {
                const productId = item.product_id;
                if (!productSales[productId]) {
                    productSales[productId] = {
                        name: item.product_name,
                        sales: 0
                    };
                }
                productSales[productId].sales += 1;
            });

            return Object.values(productSales)
                .sort((a, b) => b.sales - a.sales)
                .slice(0, 5);
        };

        // آماده کردن داده‌های نمودار برای 30 روز گذشته
        const prepareChartData = (salesData) => {
            // ایجاد آرایه از 30 روز گذشته
            const days = [];
            const salesByDay = {};
            const countByDay = {};

            for (let i = 29; i >= 0; i--) {
                const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
                const dateString = date.toLocaleDateString('fa-IR');
                days.push(dateString);
                salesByDay[dateString] = 0;
                countByDay[dateString] = 0;
            }

            // پر کردن داده‌ها
            salesData.forEach(item => {
                const itemDate = new Date(item.order_date);
                const dateString = itemDate.toLocaleDateString('fa-IR');

                if (salesByDay[dateString] !== undefined) {
                    salesByDay[dateString] += parseInt(item.item_total || 0);
                    countByDay[dateString] += 1;
                }
            });

            return {
                days,
                sales: days.map(day => salesByDay[day]),
                counts: days.map(day => countByDay[day])
            };
        };

        const chartData30d = prepareChartData(salesLast30Days);

        return {
            sales7d: totalSales7d,
            sales30d: totalSales30d,
            count7d: count7d,
            count30d: count30d,
            topProducts7d: getTopProducts(salesLast7Days),
            topProducts30d: getTopProducts(salesLast30Days),
            chartData: chartData30d
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const data = await getData(undefined, `/seller/sales/${user.email}`);

                if (data && Array.isArray(data)) {
                    const calculatedStats = calculateStats(data);
                    setStats(calculatedStats);
                    setChartData(calculatedStats?.chartData);
                } else {
                    setError("داده‌ای یافت نشد یا فرمت داده نامعتبر است");
                }
            } catch (err) {
                setError(err.message || "خطا در دریافت اطلاعات");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // تنظیمات نمودار فروش مالی
    const salesChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'فروش مالی ۳۰ روز گذشته (تومان)',
                font: {
                    size: 16
                }
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return numberWithCommas(value);
                    }
                }
            }
        }
    };

    // تنظیمات نمودار تعداد فروش
    const countChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'تعداد فروش ۳۰ روز گذشته',
                font: {
                    size: 16
                }
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    // داده‌های نمودار فروش مالی
    const salesChartData = {
        labels: chartData?.days || [],
        datasets: [
            {
                label: 'مبلغ فروش',
                data: chartData?.sales || [],
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                tension: 0.1
            }
        ]
    };

    // داده‌های نمودار تعداد فروش
    const countChartData = {
        labels: chartData?.days || [],
        datasets: [
            {
                label: 'تعداد فروش',
                data: chartData?.counts || [],
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                tension: 0.1
            }
        ]
    };

    const InfoBox = ({ value, label, timePeriod }) => (
        <div className="bg-white dark:bg-black rounded-xl border border-gray-100">
            <div className="p-4 flex gap-2 border-b-solid border-b-gray-100 border-b-2">
                <div className="bg-gray-50 rounded-full w-14 h-14 flex justify-center items-center">
                    <LiaMoneyBillWaveSolid className="text-primary-color" />
                </div>
                <div className="flex flex-col gap-1">
                    <span className="font-bold text-lg">{value}</span>
                    <span className="text-sm text-gray-400">{label}</span>
                </div>
            </div>
            <div className="p-4 flex gap-2">
                <div className="flex items-center text-sm justify-between w-full">
                    <span className="text-gray-400">تاریخ:</span>
                    <span>{`از ${timePeriod?.[0] ? timePeriod?.[0] : "ابتدا"} تا ${timePeriod?.[1] ? timePeriod?.[1] : "کنون"}`}</span>
                </div>
            </div>
        </div>
    );

    if (loading) return <div className="p-6">در حال بارگذاری...</div>;
    if (error) return <div className="p-6 text-red-500">خطا: {error}</div>;
    if (!stats) return <div className="p-6">داده‌ای برای نمایش وجود ندارد</div>;

    return (
        <div className="lg:p-3 space-y-6">
            <Hintbox text="اطلاعات فروش هر 24 ساعت یکبار بروز رسانی می‌شوند و تنها شامل فروش هایی هستند که در مرحله بسته بندی یا ارسال شده و تحویلی به دست مشتری هستند." />
            {/* چهار باکس اطلاعات */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <InfoBox
                    value={`${numberWithCommas(stats.sales7d)} تومان`}
                    label="میزان فروش ۷ روز گذشته"
                    timePeriod={["۷ روز پیش", "امروز"]}
                />
                <InfoBox
                    value={`${numberWithCommas(stats.sales30d)} تومان`}
                    label="میزان فروش ۳۰ روز گذشته"
                    timePeriod={["۳۰ روز پیش", "امروز"]}
                />
                <InfoBox
                    value={numberWithCommas(stats.count7d)}
                    label="تعداد فروش ۷ روز گذشته"
                    timePeriod={["۷ روز پیش", "امروز"]}
                />
                <InfoBox
                    value={numberWithCommas(stats.count30d)}
                    label="تعداد فروش ۳۰ روز گذشته"
                    timePeriod={["۳۰ روز پیش", "امروز"]}
                />
            </div>

            {/* نمودارها */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* نمودار فروش مالی */}
                <div className="bg-white dark:bg-black rounded-xl border border-gray-100 p-4">
                    <Line options={salesChartOptions} data={salesChartData} />
                </div>

                {/* نمودار تعداد فروش */}
                <div className="bg-white dark:bg-black rounded-xl border border-gray-100 p-4">
                    <Bar options={countChartOptions} data={countChartData} />
                </div>
            </div>

            {/* دو جدول رتبه بندی */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ۷ روز */}
                <div className="bg-white dark:bg-black rounded-xl border border-gray-100 p-4">
                    <h3 className="font-semibold mb-4">5 محصول پرفروش 7 روز گذشته</h3>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b">
                                <th className="text-right py-2">محصول</th>
                                <th className="text-right py-2">تعداد فروش</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.topProducts7d?.map((p, i) => (
                                <tr key={i} className="border-b last:border-none">
                                    <td className="py-2">{p.name}</td>
                                    <td className="py-2">{numberWithCommas(p.sales)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ۳۰ روز */}
                <div className="bg-white dark:bg-black rounded-xl border border-gray-100 p-4">
                    <h3 className="font-semibold mb-4">5 محصول پرفروش 30 روز گذشته</h3>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b">
                                <th className="text-right py-2">محصول</th>
                                <th className="text-right py-2">تعداد فروش</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.topProducts30d?.map((p, i) => (
                                <tr key={i} className="border-b last:border-none">
                                    <td className="py-2">{p.name}</td>
                                    <td className="py-2">{numberWithCommas(p.sales)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}